import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toggleErrorDialog } from 'actions';
import imageCompression from 'browser-image-compression';
import { useLazyMultipleAttachmentUploadQuery } from '../redux/hook/useBatchUpload';
import api from 'api';
import { I18n } from 'react-redux-i18n';
import {
    TICKET_FILE_EXT_EMPTY_ERROR,
    TICKET_FILE_EXT_OVER_LIMIT_ERROR,
    TICKET_FILE_EXT_INVALID_ERROR,
    MAX_IMAGE_FILE_COUNT,
    MAX_VIDEO_FILE_COUNT,
    MAX_IMAGE_FILE_SIZE,
    MAX_VIDEO_FILE_SIZE,
} from 'constants/ticket';

const getSecureRandom = () => {
    return crypto.getRandomValues(new Uint32Array(1))[0] / 0xFFFFFFFF;
};

const cloneFile = async (file) => {
    const response = new Response(file);

    const blob = await response.blob();

    return new File([blob], file.name, {
        type: file.type || 'application/octet-stream',
        lastModified: file.lastModified || Date.now(),
    });
};

const useFileUpload = (options = {}) => {
    const [multipleAttachmentUpload] = useLazyMultipleAttachmentUploadQuery();
    const urlsToCleanupRef = useRef(new Set());
    const dispatch = useDispatch();
    const {
        maxImages = MAX_IMAGE_FILE_COUNT,
        maxVideos = MAX_VIDEO_FILE_COUNT,
        maxImageSize = MAX_IMAGE_FILE_SIZE,
        maxVideoSize = MAX_VIDEO_FILE_SIZE,
        compressionOptions = {
            maxWidthOrHeight: 1500,
            useWebWorker: false,
            initialQuality: 0.6,
            maxIteration: 10
        }
    } = options;

    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [selecting, setSelecting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    const allowedImageExts = useMemo(() => ['png', 'jpg', 'jpeg'], []);
    const allowedVideoExts = useMemo(() => ['mp4', 'mov'], []);

    const getFileExtension = useCallback((filename) => {
        return filename.split('.').pop().toLowerCase();
    }, []);

    const isImage = useCallback((file) => {
        const extension = getFileExtension(file.name);
        return allowedImageExts.includes(extension);
    }, [getFileExtension, allowedImageExts]);

    const isVideo = useCallback((file) => {
        const extension = getFileExtension(file.name);
        return allowedVideoExts.includes(extension);
    }, [getFileExtension, allowedVideoExts]);

    const generateVideoThumbnail = useCallback((videoFile) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const objectUrl = URL.createObjectURL(videoFile);

            const cleanup = () => {
                video.onloadedmetadata = null;
                video.onseeked = null;
                video.onerror = null;
                video.onstalled = null;
                try {
                    URL.revokeObjectURL(objectUrl);
                } catch (_) {}
            };

            video.onloadedmetadata = () => {
                canvas.width = 300;
                canvas.height = (video.videoHeight / video.videoWidth) * 300;
                video.currentTime = Math.min(1, video.duration * 0.1);
            };

            video.onseeked = () => {
                try {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => {
                        cleanup();
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('toBlob failed'));
                        }
                    }, 'image/jpeg', 0.8);
                } catch (error) {
                    cleanup();
                    reject(error);
                }
            };

            video.onerror = () => {
                cleanup();
                reject(new Error('Video decode error'));
            };

            video.onstalled = () => {
                cleanup();
                reject(new Error('Video loading stalled'));
            };

            video.src = objectUrl;
            video.muted = true;
            video.playsInline = true;
            video.crossOrigin = 'anonymous';
            video.load();
        });
    }, []);

    const handleFileSelect = useCallback(async (event) => {
        setSelecting(true);
        const inputEl = event.target;
        const selectedFiles = Array.from(inputEl?.files || []);

        try {
            let stableFiles = [];
            try {
                stableFiles = await Promise.all(selectedFiles.map(cloneFile));
            } catch (e) {
                dispatch(toggleErrorDialog(I18n.t('general_error_message')));
                return;
            } finally {
                if (inputEl) inputEl.value = '';
            }

            const validFiles = [];
            let hasError = false;

            for (const file of stableFiles) {
                if (isImage(file)) {
                    if (validFiles.filter(f => isImage(f)).length < maxImages) {
                        if (file.size > maxImageSize) {
                            dispatch(toggleErrorDialog(I18n.t('file_size_MB_warning_with_name', { name: file.name, size: 8 })));
                            hasError = true;
                            break;
                        }
                        validFiles.push(file);
                    } else {
                        dispatch(toggleErrorDialog(I18n.t('file_images_count_exceed_warning', { count: maxImages })));
                        hasError = true;
                        break;
                    }
                } else if (isVideo(file)) {
                    if (validFiles.filter(f => isVideo(f)).length < maxVideos) {
                        if (file.size > maxVideoSize) {
                            dispatch(toggleErrorDialog(I18n.t('file_size_MB_warning_with_name', { name: file.name, size: 300 })));
                            hasError = true;
                            break;
                        }
                        validFiles.push(file);
                    } else {
                        dispatch(toggleErrorDialog(I18n.t('file_videos_count_exceed_warning', { count: maxVideos })));
                        hasError = true;
                        break;
                    }
                } else {
                    dispatch(toggleErrorDialog(I18n.t(`ticket.upload_error_${ TICKET_FILE_EXT_INVALID_ERROR }`)));
                    hasError = true;
                    break;
                }
            }

            if (hasError) {
                return;
            }

            const fileObjects = validFiles.map(file => ({
                id: Date.now() + Math.round(getSecureRandom() * 0xFFFFFFFF),
                originalFile: file,
                compressedFile: null,
                name: file.name,
                size: file.size,
                type: file.type,
                category: isImage(file) ? 'image' : 'video',
                status: 'pending',
                progress: 0,
                error: null,
                thumbnailUrl: null,
                previewUrl: null
            }));

            setFiles(fileObjects);

            for (const fileObj of fileObjects) {
                try {
                    let compressedFile;
                    let thumbnailBlob;

                    if (fileObj.category === 'image') {
                        compressedFile = await imageCompression(fileObj.originalFile, compressionOptions);
                        thumbnailBlob = compressedFile;
                    } else {
                        compressedFile = fileObj.originalFile;
                        try {
                            thumbnailBlob = await generateVideoThumbnail(fileObj.originalFile);
                        } catch (error) {
                            thumbnailBlob = compressedFile;
                        }
                    }

                    const thumbnailUrl = URL.createObjectURL(thumbnailBlob);
                    urlsToCleanupRef.current.add(thumbnailUrl);

                    const previewUrl = URL.createObjectURL(fileObj.originalFile);
                    urlsToCleanupRef.current.add(previewUrl);

                    setFiles(prev => {
                        const newFiles = prev.map(f =>
                            f.id === fileObj.id
                                ? {
                                    ...f,
                                    compressedFile,
                                    status: 'ready',
                                    compressedSize: compressedFile.size,
                                    thumbnailUrl,
                                    previewUrl
                                }
                                : f
                        );
                        return newFiles;
                    });

                } catch (error) {
                    dispatch(toggleErrorDialog(I18n.t('general_error_message')));
                    setFiles(prev => prev.map(f =>
                        f.id === fileObj.id
                            ? { ...f, status: 'error' }
                            : f
                    ));
                }
            }
        } finally {
            setSelecting(false);
        }
    }, [maxImages, maxVideos, maxImageSize, maxVideoSize, isImage, isVideo, compressionOptions, dispatch, generateVideoThumbnail]);

    const uploadFileToS3 = async (fileObj, urlInfo) => {
        try {
            setFiles(prev => prev.map(f =>
                f.id === fileObj.id
                ? { ...f, status: 'uploading', progress: 0 }
                : f
            ));

            setUploadProgress(prev => ({
                ...prev,
                [fileObj.id]: 0
            }));

            const updateProgress = (progress) => {
                setFiles(prev => prev.map(f =>
                    f.id === fileObj.id
                    ? { ...f, progress }
                    : f
                ));
                setUploadProgress(prev => ({
                    ...prev,
                    [fileObj.id]: progress
                }));
            };

            await api.uploadFile({
                url: urlInfo.url,
                file: fileObj.compressedFile,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    updateProgress(percentCompleted);
                }
            });
            setFiles(prev => prev.map(f =>
                f.id === fileObj.id
                ? {
                    ...f,
                    status: 'uploaded',
                    progress: 100,
                    storageFilename: urlInfo.storage_filename,
                    s3Url: urlInfo.url.split('?')[0]
                }
                : f
            ));

            setUploadProgress(prev => ({
                ...prev,
                [fileObj.id]: 100
            }));

            return urlInfo.storage_filename;

        } catch (error) {
            dispatch(toggleErrorDialog(I18n.t('general_error_message')));
            setFiles(prev => prev.map(f =>
                f.id === fileObj.id
                ? { ...f, status: 'ready' }
                : f
            ));
            throw error;
        }
    };

    const handleBatchUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        let message = I18n.t('general_error_message');
        try {
            const result = [];
            const filesToUpload = files.filter(f => f.status === 'ready');

            if (filesToUpload.length === 0) {
                return [];
            }

            const fileExtensions = filesToUpload.map(file => {
                const extension = file.name.split('.').pop() || file.type.split('/')[1];
                return extension;
            });

            const { data, error } = await multipleAttachmentUpload({ file_exts: fileExtensions });
            if (error) {
                const { data: errorData } = error;
                const { code } = errorData ?? {};

                if (code === TICKET_FILE_EXT_EMPTY_ERROR
                    || code === TICKET_FILE_EXT_OVER_LIMIT_ERROR
                    || code === TICKET_FILE_EXT_INVALID_ERROR ) {
                    message = I18n.t(`ticket.upload_error_${code}`);
                }
                dispatch(toggleErrorDialog(message));
                throw new Error(message);
            }

            const presignedUrls = data;

            for (let i = 0; i < filesToUpload.length; i++) {
                const file = filesToUpload[i];
                const urlInfo = presignedUrls[i];
                if (urlInfo?.url) {
                    result.push(await uploadFileToS3(file, urlInfo));
                }
            }
            return result;

        } catch (error) {
            dispatch(toggleErrorDialog(message));
            setFiles(prev => prev.map(f =>
                f.status === 'ready' || f.status === 'uploading'
                    ? { ...f, status: 'ready' }
                    : f
            ));
            throw error;
        } finally {
            setUploading(false);
        }
    };

    const removeFile = (fileId) => {
        setFiles(prev => {
            const fileToRemove = prev.find(f => f.id === fileId);
            if (fileToRemove) {
                if (fileToRemove.thumbnailUrl) {
                    URL.revokeObjectURL(fileToRemove.thumbnailUrl);
                    urlsToCleanupRef.current.delete(fileToRemove.thumbnailUrl);
                }
                if (fileToRemove.previewUrl) {
                    URL.revokeObjectURL(fileToRemove.previewUrl);
                    urlsToCleanupRef.current.delete(fileToRemove.previewUrl);
                }
            }
            return prev.filter(f => f.id !== fileId);
        });
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
        });
    };

    const clearAllFiles = () => {
        urlsToCleanupRef.current.forEach(url => {
            URL.revokeObjectURL(url);
        });
        urlsToCleanupRef.current.clear();

        setFiles([]);
        setUploadProgress({});
    };

    useEffect(() => {
        const urlsToCleanup = urlsToCleanupRef.current;

        return () => {
            urlsToCleanup.forEach(url => {
                URL.revokeObjectURL(url);
            });
            urlsToCleanup.clear();
        };
    }, []);

    const imageFiles = files.filter(f => f.category === 'image');
    const videoFiles = files.filter(f => f.category === 'video');
    const uploadedFiles = files.filter(f => f.status === 'uploaded');
    const totalProgress = files.length > 0
        ? Math.round(files.reduce((sum, file) => sum + file.progress, 0) / files.length)
        : 0;

    return {
        files,
        uploading,
        selecting,
        uploadProgress,
        imageFiles,
        videoFiles,
        uploadedFiles,
        totalProgress,
        handleFileSelect,
        handleBatchUpload,
        removeFile,
        clearAllFiles,
        maxImages,
        maxVideos,
        maxImageSize,
        maxVideoSize,
    };
};

export default useFileUpload;
